'use client'

import { frameTypeAtom } from 'features/generator/pages/QrDesignFormPage/FrameDesignForm'
import {
	qrLogoImageAtom,
	qrLogoImageFileAtom,
} from 'features/generator/pages/QrDesignFormPage/LogoDesignForm'
import { FRAME_TYPE_OPTION_LIST } from 'features/generator/qrFrames'
import { useAtomValue } from 'jotai'
import { useEffect, useState } from 'react'

import { areAllColorsWhite, createQrImageBuilder } from '../qrImageBuilder'
import { TQrDesign } from '../qrImageTypes'
import { ImageWithFrame } from './ImageWithFrame'
import { NoFrameQrImage } from './NoFrameQrImage'

let cachedCreateQrImageBuilder: typeof createQrImageBuilder | null = null

export const Image = ({
	data,
	qrDesign,
	size,
	className,
}: {
	data: string
	qrDesign: TQrDesign
	size: number
	className?: string
}) => {
	const [patternMarkup, setPatternMarkup] = useState<string>('')
	const allWhiteColors = areAllColorsWhite(qrDesign)
	const logoImage = useAtomValue(qrLogoImageAtom)
	const logoFile = useAtomValue(qrLogoImageFileAtom)
	const localLogoFile = logoImage ? null : logoFile

	useEffect(() => {
		;(async () => {
			if (!cachedCreateQrImageBuilder) {
				const qrImageBuilderModule = await import('../qrImageBuilder')
				cachedCreateQrImageBuilder = qrImageBuilderModule.createQrImageBuilder
			}

			const qrImageBuilder = await cachedCreateQrImageBuilder(
				{ ...qrDesign, data },
				localLogoFile,
			)

			const patternMarkup = await qrImageBuilder.getPatternSvg()
			setPatternMarkup(patternMarkup)
		})()
	}, [data, qrDesign, localLogoFile])

	const frameType = useAtomValue(frameTypeAtom)

	if (!patternMarkup) {
		return null
	}

	const selectedFrameOption = FRAME_TYPE_OPTION_LIST.find(
		({ value }: { value: string }) => value === frameType,
	)

	const Frame = selectedFrameOption?.Frame

	const noFrame = frameType === 'default' || !Frame

	if (noFrame) {
		return (
			<NoFrameQrImage
				patternMarkup={patternMarkup}
				size={size}
				className={className}
			/>
		)
	}

	return (
		<ImageWithFrame
			isWhite={allWhiteColors}
			patternMarkup={patternMarkup}
			size={size}
			className={className}
		/>
	)
}
